import { useQuery } from "@tanstack/react-query";

import SubscriptionService from "@/services/SubscriptionService";

import { useAuth } from "@/hooks/useAuth";

import { subscriptionKeys } from "@/constants/CQueryKeys";

import {
	TPlan,
	TPlanGateKey,
	TSubscription,
	TSubscriptionState,
	TUsageKey,
	TPlanGate,
} from "@/types/TSubscription";

/** Whole days until `iso`, never negative; 0 when past or missing. */
export const daysLeft = (
	iso: string | null | undefined,
	now = new Date()
): number => {
	if (!iso) return 0;
	const ms = new Date(iso).getTime() - now.getTime();
	return Math.max(0, Math.ceil(ms / 86_400_000));
};

/**
 * A paying customer changes plan through the Stripe portal (it
 * prorates); everyone else — free, trial, lapsed — goes through Checkout.
 */
export const goesThroughPortal = (
	subscription: TSubscription | null
): boolean =>
	!!subscription?.hasCustomer &&
	(subscription.status === "active" || subscription.status === "past_due");

/** A local trial still running: `trialing` with nothing at Stripe to end it. */
export const isTrialing = (subscription: TSubscription | null | undefined) =>
	subscription?.status === "trialing" && !subscription.hasCustomer;

/**
 * The plan in force and what is left of it. Fetched once the session is
 * open; the views that gate a button read `usage`, the profile shows the
 * lot. Refetched after a checkout returns (SubscriptionSuccess) and when a
 * 402 comes in, since the limit it names may have just been reached.
 */
export const useSubscription = (workspaceId?: number) => {
	const { user } = useAuth();

	// The user's own workspace unless a page names another: the gates of
	// the app read their own plan; /workspace and /subscription pick one.
	const query = useQuery<TSubscriptionState>({
		queryKey:
			workspaceId === undefined
				? subscriptionKeys.current
				: subscriptionKeys.of(workspaceId),
		queryFn: () => SubscriptionService.fetch(workspaceId),
		enabled: !!user,
		staleTime: 60_000,
	});

	const state = query.data;

	/** UX only — the API is the guard. `true` while unknown, so nothing is disabled on a hunch. */
	const canCreate = (key: TUsageKey): boolean => {
		const usage = state?.usage[key];
		if (!usage || usage.max === null) return true;
		return usage.used < usage.max;
	};

	/**
	 * What a gated button needs to know: whether it is locked, the plan
	 * that unlocks it (from the API, never recomputed here), and the
	 * count behind a limit. Open while the answer is not in.
	 */
	const gate = (key: TPlanGateKey): TPlanGate => {
		if (!state) return { locked: false, requiredPlan: null, usage: null };
		if (key === "pdf" || key === "invoices" || key === "sharing") {
			const feature = state.features[key];
			return {
				locked: !feature.enabled,
				requiredPlan: feature.requiredPlan,
				usage: null,
			};
		}
		const usage = state.usage[key];
		return {
			locked: !canCreate(key),
			requiredPlan: usage.requiredPlan,
			usage,
		};
	};

	return {
		...query,
		state,
		plan: state?.plan,
		subscription: state?.subscription ?? null,
		canCreate,
		gate,
		canExportPdf: state?.plan.canExportPdf ?? true,
		canShare: state?.plan.canShare ?? true,
		canInvoice: state?.plan.canInvoice ?? true,
	};
};

/**
 * The catalogue, for the upgrade grid. Same for everyone and only ever
 * changed at deploy time, so it is fetched once per session and kept.
 */
export const usePlans = (enabled = true) =>
	useQuery<TPlan[]>({
		queryKey: subscriptionKeys.plans,
		queryFn: () => SubscriptionService.plans(),
		enabled,
		staleTime: Infinity,
	});
